import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ATMButton, ATMModal } from 'shared-it-appmod-ui';
import {
  ToastSuccess,
  ToastError,
} from 'src/components/atoms/toaster/toaster.component';
import Lang from 'src/libraries/language';
import { getUser } from 'src/libraries/auth.library';
import { AccessRole } from 'src/constants';
import { IAdminRole, IAdminForm } from 'src/models/admin.model';
import { useAdminContext } from 'src/contexts/admin.context';
import { useEmployeeContext } from 'src/contexts/employee.context';
import { IEmployee } from 'src/models/employee.model';
import { getAdminStatus } from 'src/selectors/admin.selector';
import { actionTypes } from 'src/ducks/admin.duck';
import RoleMaintenanceForm from '../role-maintenance-form/role-maintenance-form.component';
import style from '../role-maintenance-list/role-maintenance-list.module.scss';

type IProp = {
  data: IAdminRole;
};

const RoleMaintenanceEdit: React.FC<IProp> = ({ data }) => {
  const { state, actions } = useAdminContext();
  const { state: employeeState } = useEmployeeContext();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [employee, setEmployee] = useState<IEmployee>();
  const [isDirty, setIsDirty] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const defaultValues: Partial<IAdminForm> = {
    empNo: data.empNo,
    roleId: data.roleId,
  };

  useEffect(() => {
    const user = employeeState.list.find(
      (value) => value.emp_no === data.empNo
    );

    setEmployee(user);
  }, [employeeState, data, setEmployee]);

  const editStatus = getAdminStatus(state, actionTypes.ADMIN_DATA_UPDATE);

  const handleSave = useCallback(() => {
    if (formRef && formRef.current) {
      formRef.current.handleSubmit();
    }
  }, [formRef]);

  const handleSubmit = useCallback(
    async (value) => {
      const user = getUser();

      const formData: IAdminForm = {
        empNo: value.empNo,
        roleId: value.roleId,
      };

      const response = await actions.updatePUT(formData);

      if (response.payload) {
        ToastSuccess(
          Lang.formatString(
            Lang.MSG_ADMIN_SUCCESS_MODIFY,
            `${employee?.last_name}, ${employee?.first_name}`
          )
        );

        if (
          user?.emp_no === value.empNo &&
          response.payload.user.roleId === AccessRole.ADMIN
        ) {
          window.location.reload();
        } else {
          setIsEditOpen(false);
          setEmployee(undefined);
          actions.listGET();
        }
      } else {
        ToastError(response.error.message);
      }
    },
    [actions, employee, setIsDirty]
  );

  const handleSearch = useCallback(
    (id) => {
      const user = employeeState.list.find((value) => value.emp_no === id);

      setEmployee(user);
    },
    [employeeState, setEmployee]
  );

  return (
    <ATMModal
      open={isEditOpen}
      size="tiny"
      trigger={
        <ATMButton
          className={style.actionIcon}
          basic
          icon="edit outline"
          onClick={() => {
            setIsEditOpen(true);
            setEmployee(
              employeeState.searchList.find(
                (value) => value.emp_no === data.empNo
              )
            );
          }}
        />
      }
      header={Lang.LBL_ADMIN_EDIT}
      content={
        <div className={style.content}>
          <RoleMaintenanceForm
            defaultValues={defaultValues}
            formRef={formRef}
            employee={employee}
            disableField
            handleSubmit={handleSubmit}
            handleSearch={handleSearch}
            handleEnable={(value) => setIsDirty(value)}
          />
        </div>
      }
      actions={
        <div className={style.actionBtns}>
          <ATMButton
            basic
            color="blue"
            content={Lang.LBL_CANCEL}
            onClick={() => {
              setIsEditOpen(false);
              setEmployee(undefined);
            }}
          />
          <ATMButton
            primary
            content={Lang.LBL_SAVE}
            disabled={!isDirty || editStatus.fetching}
            onClick={handleSave}
            loading={editStatus.fetching}
          />
        </div>
      }
    />
  );
};

export default RoleMaintenanceEdit;
